import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

/**
 * BackgroundMusic
 *
 * A singleton ambient-music player that lives at the App shell level,
 * so it keeps playing across route changes instead of restarting on
 * every navigation. Behaviour:
 *
 *  • Reads the user's saved preference from localStorage
 *    ('levanta:music' === 'on' or 'off'). Defaults to OFF, because
 *    autoplay-with-sound is blocked in all modern browsers until the
 *    user explicitly opts in.
 *  • Exposes a floating round button in the bottom-right corner of
 *    every page. One tap toggles sound on / off and persists the
 *    choice. A small "try me" pulse plays once on first visit so the
 *    control is discoverable without shouting.
 *  • Loops <audio> at half volume so it's ambient, not foreground.
 *  • Respects the user's "prefers-reduced-motion" preference — we
 *    skip the pulse animation in that case.
 *
 * The component is mount-once-only. It is intentionally NOT placed
 * inside the nav or any route so it never unmounts, which is what
 * guarantees uninterrupted playback between pages.
 */
const STORAGE_KEY = 'levanta:music';
const AUDIO_SRC = '/ambient.mp3';
const VOLUME = 0.35;

const BackgroundMusic = () => {
  const audioRef = useRef(null);
  const [isOn, setIsOn] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      // Default to ON. Only stay OFF if the user has explicitly opted out
      // in a previous session. Browsers block hands-off autoplay without
      // a user gesture, but we keep the toggle in the ON state visually
      // and arm a one-time gesture listener below that starts playback
      // the first time the visitor taps / scrolls / clicks anywhere.
      return window.localStorage.getItem(STORAGE_KEY) !== 'off';
    } catch (_) {
      return true;
    }
  });
  const [hasInteracted, setHasInteracted] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(`${STORAGE_KEY}:seen`) === '1';
    } catch (_) {
      return false;
    }
  });

  // Drive playback from the isOn flag. When ON we try play() — browsers
  // will reject the very first attempt without a user gesture, so we arm
  // a single one-shot listener (click / touch / keydown / scroll) that
  // retries once and removes itself. From then on the audio just plays.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    el.volume = VOLUME;
    if (!isOn) {
      el.pause();
      return undefined;
    }
    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.then === 'function') {
        return p.then(() => true).catch(() => false);
      }
      return Promise.resolve(true);
    };
    let cleanup = () => {};
    tryPlay().then((ok) => {
      if (ok) return;
      const events = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
      const onFirstGesture = () => {
        events.forEach((e) => window.removeEventListener(e, onFirstGesture));
        tryPlay();
      };
      events.forEach((e) =>
        window.addEventListener(e, onFirstGesture, { once: true, passive: true })
      );
      cleanup = () => events.forEach((e) => window.removeEventListener(e, onFirstGesture));
    });
    return cleanup;
  }, [isOn]);

  // Allow other parts of the app (e.g. the homepage teaser modal) to
  // ask the ambient music to duck out while another sound is playing,
  // then resume. Keeps the "Music on" preference intact.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onPause = () => el.pause();
    const onResume = () => {
      if (isOn) el.play().catch(() => {});
    };
    window.addEventListener('lv-music-pause', onPause);
    window.addEventListener('lv-music-resume', onResume);
    return () => {
      window.removeEventListener('lv-music-pause', onPause);
      window.removeEventListener('lv-music-resume', onResume);
    };
  }, [isOn]);

  const toggle = () => {
    setIsOn((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
        window.localStorage.setItem(`${STORAGE_KEY}:seen`, '1');
      } catch (_) {
        // ignore — private mode / storage disabled
      }
      return next;
    });
    setHasInteracted(true);
  };

  return (
    <>
      {/* Scoped styles — pulsing halo for the discovery affordance. */}
      <style>{`
        @keyframes lv_music_pulse {
          0%   { box-shadow: 0 0 0 0 rgba(74, 155, 232, 0.55); }
          70%  { box-shadow: 0 0 0 14px rgba(74, 155, 232, 0); }
          100% { box-shadow: 0 0 0 0 rgba(74, 155, 232, 0); }
        }
        .lv-music-btn {
          position: fixed;
          right: 20px;
          bottom: 20px;
          z-index: 70;
          width: 44px;
          height: 44px;
          border-radius: 9999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #0b1f3a;
          color: #ffffff;
          box-shadow: 0 10px 25px -12px rgba(11, 31, 58, 0.45);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: transform .2s ease, background-color .2s ease;
        }
        .lv-music-btn:hover { background: #1e3a6f; transform: translateY(-1px); }
        .lv-music-btn.is-on { background: #4A9BE8; }
        .lv-music-btn.is-on:hover { background: #2563eb; }
        .lv-music-btn.is-idle {
          animation: lv_music_pulse 2.2s ease-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .lv-music-btn.is-idle { animation: none; }
        }
        @media (min-width: 768px) {
          .lv-music-btn { right: 28px; bottom: 28px; width: 48px; height: 48px; }
        }
      `}</style>

      {/* Single <audio> element — render once, never unmount. */}
      <audio
        ref={audioRef}
        src={AUDIO_SRC}
        loop
        preload="auto"
        data-testid="bg-music-audio"
      />

      <button
        type="button"
        onClick={toggle}
        aria-pressed={isOn}
        aria-label={isOn ? 'Mute background music' : 'Play background music'}
        title={isOn ? 'Mute music' : 'Play music'}
        data-testid="bg-music-toggle"
        className={`lv-music-btn ${isOn ? 'is-on' : ''} ${!isOn && !hasInteracted ? 'is-idle' : ''}`}
      >
        {isOn ? <Volume2 className="w-4 h-4 md:w-5 md:h-5" /> : <VolumeX className="w-4 h-4 md:w-5 md:h-5" />}
      </button>
    </>
  );
};

export default BackgroundMusic;
